/**
 * Shared image utility functions for the Nanokart UI.
 *
 * Centralises compression logic so every upload point (CustomerTryOnStudio,
 * PublicTryOnStudio, PublicTryOn, …) behaves identically.
 */

/** Target upload size — matches AWS API Gateway / Lambda payload limit */
export const COMPRESS_TARGET_BYTES = 1024 * 1024; // 1 MB

/**
 * Catalog upload safety net.
 * AWS API Gateway → Lambda has a hard ~6 MB sync-invoke payload cap, so any
 * dress/inventory image must come down to <6 MB before it leaves the browser.
 * We use 4 MB to keep a safety margin for multipart-form overhead and headers.
 */
export const CATALOG_TARGET_BYTES = 4 * 1024 * 1024; // 4 MB
export const CATALOG_MAX_DIMENSION = 2400; // preserves catalog detail

/**
 * WhatsApp-style image compression.
 *
 * Algorithm:
 *   1. If the file is already a JPEG and under targetBytes → return as-is.
 *   2. Resize so the longest side is ≤ maxDimension px (preserves aspect ratio).
 *   3. Re-encode as JPEG using a quality ladder: 0.92 → 0.85 → 0.78 → 0.70 → 0.62.
 *      Stop as soon as the blob fits within targetBytes.
 *   4. If still too large at quality 0.62, reduce canvas dimensions by 70 % and
 *      encode once more at quality 0.82.
 *   5. On any unexpected error the original file is returned unchanged.
 *
 * Why this approach?
 *   - Preserves enough body-detail for accurate virtual try-on and size AI.
 *   - Mirrors the WhatsApp heuristic (large image → JPEG, quality ladder).
 *   - Pure browser-side Canvas API — no extra dependencies.
 *
 * @param {File}   file        - Input image (any browser-supported format).
 * @param {number} [targetBytes=COMPRESS_TARGET_BYTES] - Max output bytes.
 * @param {number} [maxDimension=1920]                 - Max longest-side pixels.
 * @returns {Promise<File>}    Compressed JPEG File, or original File on error.
 */
export async function compressImageForUpload(file, targetBytes = COMPRESS_TARGET_BYTES, maxDimension = 1920) {
  const isJpeg = file.type === 'image/jpeg' || file.type === 'image/jpg';
  if (file.size <= targetBytes && isJpeg) return file;

  return new Promise((resolve) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(file); // fallback — return original
    };

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);

      const MAX_DIM = maxDimension;
      let w = img.naturalWidth;
      let h = img.naturalHeight;
      if (w > MAX_DIM || h > MAX_DIM) {
        if (w >= h) { h = Math.round(h * MAX_DIM / w); w = MAX_DIM; }
        else        { w = Math.round(w * MAX_DIM / h); h = MAX_DIM; }
      }

      const canvas = document.createElement('canvas');
      canvas.width  = w;
      canvas.height = h;
      canvas.getContext('2d').drawImage(img, 0, 0, w, h);

      const outName = file.name.replace(/\.[^.]+$/, '.jpg') || 'photo.jpg';

      const tryQuality = (qualities, idx) => {
        if (idx >= qualities.length) {
          // All quality levels exhausted — reduce dimensions by 70 % and encode once more
          const sw = Math.round(w * 0.7);
          const sh = Math.round(h * 0.7);
          const c2 = document.createElement('canvas');
          c2.width  = sw;
          c2.height = sh;
          c2.getContext('2d').drawImage(canvas, 0, 0, sw, sh);
          c2.toBlob(
            blob => resolve(new File([blob], outName, { type: 'image/jpeg' })),
            'image/jpeg',
            0.82
          );
          return;
        }
        canvas.toBlob(blob => {
          if (blob.size <= targetBytes) {
            resolve(new File([blob], outName, { type: 'image/jpeg' }));
          } else {
            tryQuality(qualities, idx + 1);
          }
        }, 'image/jpeg', qualities[idx]);
      };

      tryQuality([0.92, 0.85, 0.78, 0.70, 0.62], 0);
    };

    img.src = objectUrl;
  });
}

/**
 * Catalog (dress / inventory) image compression.
 *
 * Wraps `compressImageForUpload` with a 4 MB target and a 2400 px max
 * dimension so:
 *   • images stay safely under AWS API Gateway's 6 MB hard cap, and
 *   • product detail is preserved (catalog imagery needs higher fidelity than
 *     the 1 MB / 1920 px target used for try-on selfies).
 *
 * @param {File} file
 * @returns {Promise<File>}
 */
export async function compressForCatalog(file) {
  return compressImageForUpload(file, CATALOG_TARGET_BYTES, CATALOG_MAX_DIMENSION);
}
